import { Currency, CurrencyAmount, currencyEquals, ETHER, Token } from '@uniswap/sdk'
import React, { CSSProperties, MutableRefObject, useCallback, useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { FixedSizeList } from 'react-window'
import { Text } from 'rebass'
import styled from 'styled-components'
import { useActiveWeb3React } from '../../hooks'
import { useSelectedTokenList, WrappedTokenInfo } from '../../state/lists/hooks'
import { useAddUserToken, useRemoveUserAddedToken } from '../../state/user/hooks'
import { isWarningToken, isSpamToken, getRiskLevelText } from '../../constants/tokenRiskLevel'
import { AlertTriangle, AlertOctagon } from 'react-feather'
import { useCurrencyBalance, useResilientSingleTokenBalance } from '../../state/wallet/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { useIsUserAddedToken } from '../../hooks/Tokens'
import Column from '../Column'
import { RowFixed } from '../Row'
import CurrencyLogo from '../CurrencyLogo'
import { MouseoverTooltip } from '../Tooltip'
import { FadedSpan, MenuItem } from './styleds'
import Loader from '../Loader'
import { isTokenOnList } from '../../utils'

function currencyKey(currency: Currency): string {
  return currency instanceof Token ? currency.address : currency === ETHER ? 'ETHER' : ''
}

const StyledBalanceText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  max-width: 5rem;
  text-overflow: ellipsis;
`

const Tag = styled.div`
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text2};
  font-size: 14px;
  border-radius: 4px;
  padding: 0.25rem 0.3rem 0.25rem 0.3rem;
  max-width: 6rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  justify-self: flex-end;
  margin-right: 4px;
`

const RiskBadge = styled.div<{ severity: 'warning' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${({ theme, severity }) => (severity === 'danger' ? theme.red1 + '15' : theme.yellow2 + '15')};
  color: ${({ theme, severity }) => (severity === 'danger' ? theme.red1 : theme.yellow2)};
  border: 1px solid ${({ theme, severity }) => (severity === 'danger' ? theme.red1 + '40' : theme.yellow2 + '40')};
  margin-left: 8px;
`

function Balance({ balance }: { balance: CurrencyAmount }) {
  return <StyledBalanceText title={balance.toExact()}>{balance.toExact()}</StyledBalanceText>
}

const TagContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`

function TokenTags({ currency }: { currency: Currency }) {
  if (!(currency instanceof WrappedTokenInfo)) {
    return <span />
  }

  const tags = currency.tags
  if (!tags || tags.length === 0) return <span />

  const tag = tags[0]

  return (
    <TagContainer>
      <MouseoverTooltip text={tag.description}>
        <Tag key={tag.id}>{tag.name}</Tag>
      </MouseoverTooltip>
      {tags.length > 1 ? (
        <MouseoverTooltip
          text={tags
            .slice(1)
            .map(({ name, description }) => `${name}: ${description}`)
            .join('; \n')}
        >
          <Tag>...</Tag>
        </MouseoverTooltip>
      ) : null}
    </TagContainer>
  )
}

// 为高风险代币添加特殊样式的 MenuItem
const RiskMenuItem = styled(MenuItem)<{ riskLevel?: 'warning' | 'danger' | 'none' }>`
  background-color: ${({ theme, riskLevel }) =>
    riskLevel === 'danger' ? theme.red1 + '08' : riskLevel === 'warning' ? theme.yellow2 + '08' : 'transparent'};
  border-left: 3px solid
    ${({ theme, riskLevel }) =>
      riskLevel === 'danger' ? theme.red1 : riskLevel === 'warning' ? theme.yellow2 : 'transparent'};
`

function CurrencyRow({
  currency,
  onSelect,
  isSelected,
  otherSelected,
  style
}: {
  currency: Currency
  onSelect: () => void
  isSelected: boolean
  otherSelected: boolean
  style: CSSProperties
}) {
  const { t } = useTranslation()
  const { account, chainId } = useActiveWeb3React()
  const key = currencyKey(currency)
  const selectedTokenList = useSelectedTokenList()
  const isOnSelectedList = isTokenOnList(selectedTokenList, currency)
  const customAdded = useIsUserAddedToken(currency)

  // 对于代币使用单独请求，对于ETH仍使用原有逻辑
  const isToken = currency instanceof Token
  const ethBalance = useCurrencyBalance(account ?? undefined, isToken ? undefined : currency)
  const { balance: tokenBalance, loading: tokenLoading, error: tokenError, retry } = useResilientSingleTokenBalance(
    account ?? undefined,
    isToken ? (currency as Token) : undefined
  )

  const balance = isToken ? tokenBalance : ethBalance
  const isLoading = isToken ? tokenLoading : false

  const removeToken = useRemoveUserAddedToken()
  const addToken = useAddUserToken()

  // 判断风险等级
  const riskLevel = currency instanceof WrappedTokenInfo ? currency.riskLevel : undefined
  const riskSeverity = isSpamToken(riskLevel) ? 'danger' : isWarningToken(riskLevel) ? 'warning' : 'none'

  // only show add or remove buttons if not on selected list
  return (
    <RiskMenuItem
      style={style}
      className={`token-item-${key}`}
      onClick={() => (isSelected ? null : onSelect())}
      disabled={isSelected}
      selected={otherSelected}
      riskLevel={riskSeverity}
    >
      <CurrencyLogo currency={currency} size={'24px'} />
      <Column>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Text title={currency.name} fontWeight={500}>
            {currency.symbol}
          </Text>
          {currency instanceof WrappedTokenInfo &&
            currency.riskLevel &&
            (isWarningToken(currency.riskLevel) || isSpamToken(currency.riskLevel)) && (
              <RiskBadge severity={isSpamToken(currency.riskLevel) ? 'danger' : 'warning'}>
                {isSpamToken(currency.riskLevel) ? <AlertOctagon size={10} /> : <AlertTriangle size={10} />}
                {getRiskLevelText(currency.riskLevel, t)}
              </RiskBadge>
            )}
        </div>
        <FadedSpan>
          {!isOnSelectedList && customAdded ? (
            <TYPE.main fontWeight={500}>
              {t('addedByUser')}
              <LinkStyledButton
                onClick={event => {
                  event.stopPropagation()
                  if (chainId && currency instanceof Token) removeToken(chainId, currency.address)
                }}
              >
                ({t('remove')})
              </LinkStyledButton>
            </TYPE.main>
          ) : null}
          {!isOnSelectedList && !customAdded ? (
            <TYPE.main fontWeight={500}>
              {t('foundByAddress')}
              <LinkStyledButton
                onClick={event => {
                  event.stopPropagation()
                  if (currency instanceof Token) addToken(currency)
                }}
              >
                ({t('add')})
              </LinkStyledButton>
            </TYPE.main>
          ) : null}
        </FadedSpan>
      </Column>
      <TokenTags currency={currency} />
      <RowFixed style={{ justifySelf: 'flex-end' }}>
        {balance ? (
          <Balance balance={balance} />
        ) : account ? (
          isLoading ? (
            <Loader />
          ) : tokenError && isToken ? (
            <LinkStyledButton
              onClick={event => {
                event.stopPropagation()
                retry()
              }}
              style={{ fontSize: '12px', color: 'red' }}
            >
              {t('retry')}
            </LinkStyledButton>
          ) : (
            <Loader />
          )
        ) : null}
      </RowFixed>
    </RiskMenuItem>
  )
}

export default function CurrencyList({
  height,
  currencies,
  selectedCurrency,
  onCurrencySelect,
  otherCurrency,
  fixedListRef,
  showETH
}: {
  height: number
  currencies: Currency[]
  selectedCurrency?: Currency | null
  onCurrencySelect: (currency: Currency) => void
  otherCurrency?: Currency | null
  fixedListRef?: MutableRefObject<FixedSizeList | undefined>
  showETH: boolean
}) {
  const itemData = useMemo(() => (showETH ? [Currency.ETHER, ...currencies] : currencies), [currencies, showETH])
  const containerRef = useRef<HTMLDivElement>(null)

  const Row = useCallback(
    ({ data, index, style }) => {
      const currency: Currency = data[index]
      const isSelected = Boolean(selectedCurrency && currencyEquals(selectedCurrency, currency))
      const otherSelected = Boolean(otherCurrency && currencyEquals(otherCurrency, currency))
      const handleSelect = () => onCurrencySelect(currency)
      return (
        <CurrencyRow
          style={style}
          currency={currency}
          isSelected={isSelected}
          onSelect={handleSelect}
          otherSelected={otherSelected}
        />
      )
    },
    [onCurrencySelect, otherCurrency, selectedCurrency]
  )

  const itemKey = useCallback((index: number, data: any) => currencyKey(data[index]), [])

  // 防止移动端下拉刷新
  useEffect(() => {
    let touchStartY = 0
    let touchMoveY = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!containerRef.current) return

      touchMoveY = e.touches[0].clientY
      const scrollTop = (fixedListRef?.current as any)?._outerRef?.scrollTop || 0

      // 如果在顶部且向下滑动，阻止默认行为
      if (scrollTop <= 0 && touchMoveY > touchStartY) {
        e.preventDefault()
      }
    }

    // 获取列表容器元素
    const getListElement = () => {
      return (fixedListRef?.current as any)?._outerRef
    }

    const listElement = getListElement()
    if (listElement) {
      // 添加 passive: false 以允许 preventDefault
      listElement.addEventListener('touchstart', handleTouchStart, { passive: false })
      listElement.addEventListener('touchmove', handleTouchMove, { passive: false })
    }

    // 设置 CSS 属性防止过度滚动
    const bodyStyle = document.body.style as any
    const originalOverscrollBehavior = bodyStyle.overscrollBehavior
    bodyStyle.overscrollBehavior = 'contain'

    return () => {
      const listEl = getListElement()
      if (listEl) {
        listEl.removeEventListener('touchstart', handleTouchStart)
        listEl.removeEventListener('touchmove', handleTouchMove)
      }
      bodyStyle.overscrollBehavior = originalOverscrollBehavior
    }
  }, [fixedListRef])

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overscrollBehavior: 'contain' } as any}>
      <FixedSizeList
        height={height}
        ref={fixedListRef as any}
        width="100%"
        itemData={itemData}
        itemCount={itemData.length}
        itemSize={56}
        itemKey={itemKey}
        style={{ overscrollBehavior: 'contain' } as any}
      >
        {Row}
      </FixedSizeList>
    </div>
  )
}
